import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import filterIcon from 'assets/images/icons/filter.svg';
import ITSVG from 'common/helpers/ITSVG';
import { CustomAutocomplete } from 'common/partials/CustomAutocomplete';
import { NotificationHistoryFilter, NotificationChannel, NotificationStatus, DateRangePreset } from '../models/notification-history.model';

interface Props {
    filters: NotificationHistoryFilter;
    onFilterChange: (filters: NotificationHistoryFilter) => void;
}

interface ChannelOption {
    label: string;
    value?: number;
}

interface StatusOption {
    label: string;
    value?: number;
}

interface DateRangeOption {
    label: string;
    value: DateRangePreset;
}

export function NotificationHistoryFilterDropdown({ filters, onFilterChange }: Props) {
    const { t } = useTranslation();
    const [localFilters, setLocalFilters] = useState<NotificationHistoryFilter>(filters);
    const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>(DateRangePreset.Custom);

    const channelOptions: ChannelOption[] = [
        { label: t('generalMessages.all'), value: undefined },
        { label: t('notificationHistory.channels.email'), value: NotificationChannel.Email },
        { label: t('notificationHistory.channels.teams'), value: NotificationChannel.Teams },
        { label: t('notificationHistory.channels.app'), value: NotificationChannel.App }
    ];

    const statusOptions: StatusOption[] = [
        { label: t('generalMessages.all'), value: undefined },
        { label: t('notificationHistory.status.pending'), value: NotificationStatus.Pending },
        { label: t('notificationHistory.status.sent'), value: NotificationStatus.Sent },
        { label: t('notificationHistory.status.failed'), value: NotificationStatus.Failed }
    ];

    const dateRangeOptions: DateRangeOption[] = [
        { label: t('notificationHistory.dateRange.all'), value: DateRangePreset.Custom },
        { label: t('notificationHistory.dateRange.last24Hours'), value: DateRangePreset.Last24Hours },
        { label: t('notificationHistory.dateRange.last7Days'), value: DateRangePreset.Last7Days },
        { label: t('notificationHistory.dateRange.last15Days'), value: DateRangePreset.Last15Days },
        { label: t('notificationHistory.dateRange.last30Days'), value: DateRangePreset.Last30Days },
        { label: t('notificationHistory.dateRange.last6Months'), value: DateRangePreset.Last6Months }
    ];

    useEffect(() => {
        const now = new Date();
        let startDate: Date | undefined;
        let endDate: Date | undefined;

        switch (dateRangePreset) {
            case DateRangePreset.Last24Hours:
                startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                endDate = now;
                break;
            case DateRangePreset.Last7Days:
                startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                endDate = now;
                break;
            case DateRangePreset.Last15Days:
                startDate = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000);
                endDate = now;
                break;
            case DateRangePreset.Last30Days:
                startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                endDate = now;
                break;
            case DateRangePreset.Last6Months:
                startDate = new Date(now.getTime() - 6 * 30 * 24 * 60 * 60 * 1000);
                endDate = now;
                break;
            case DateRangePreset.Custom:
                startDate = undefined;
                endDate = undefined;
                break;
        }

        setLocalFilters(prev => ({
            ...prev,
            startDate,
            endDate
        }));
    }, [dateRangePreset]);

    const getFilterButtonName = () => {
        const activeFilters = [
            dateRangeOptions.find(opt => opt.value === dateRangePreset)?.label,
            channelOptions.find(opt => opt.value === filters.channelId)?.label,
            statusOptions.find(opt => opt.value === filters.statusId)?.label,
        ].filter(Boolean);

        return activeFilters.length > 0 ? activeFilters.join(' | ') : t('generalMessages.filter');
    };

    const handleApplyFilters = () => {
        onFilterChange(localFilters);
    };

    const handleResetFilters = () => {
        const resetFilters: NotificationHistoryFilter = {
            ...localFilters,
            startDate: undefined,
            endDate: undefined,
            channelId: undefined,
            statusId: undefined,
            pageNumber: 1
        };
        setLocalFilters(resetFilters);
        setDateRangePreset(DateRangePreset.Custom);
        onFilterChange(resetFilters);
    };

    return (
        <div className='me-4'>
            <button
                type='button'
                className='btn btn-sm btn-flex btn-light btn-active-primary fw-bolder'
                data-kt-menu-trigger='click'
                data-kt-menu-placement='bottom-end'
                data-kt-menu-flip='top-end'
            >
                <ITSVG path={filterIcon} className='svg-icon-5 svg-icon-gray-500 me-1' />
                {getFilterButtonName()}
            </button>

            <div className='menu menu-sub menu-sub-dropdown w-250px w-md-300px' data-kt-menu='true'>
                <div className='px-7 py-5'>
                    <div className='fs-5 text-dark fw-bolder'>{t('generalMessages.filterOptions')}</div>
                </div>

                <div className='separator border-gray-200'></div>

                <div className='px-7 py-5'>
                    <div className='mb-10'>
                        <CustomAutocomplete
                            id="dateRange"
                            label={t('notificationHistory.dateRange.title')}
                            options={dateRangeOptions}
                            value={dateRangeOptions.find(opt => opt.value === dateRangePreset) || null}
                            getOptionLabel={(option) => option.label}
                            onChange={(e, value) => {
                                setDateRangePreset(value?.value || DateRangePreset.Custom);
                            }}
                        />
                    </div>

                    <div className='mb-10'>
                        <CustomAutocomplete
                            id="channel"
                            label={t('notificationHistory.channels.title')}
                            options={channelOptions}
                            value={channelOptions.find(opt => opt.value === localFilters.channelId) || null}
                            getOptionLabel={(option) => option.label}
                            onChange={(e, value) => {
                                setLocalFilters({
                                    ...localFilters,
                                    channelId: value?.value
                                });
                            }}
                        />
                    </div>

                    <div className='mb-10'>
                        <CustomAutocomplete
                            id="status"
                            label={t('notificationHistory.status.title')}
                            options={statusOptions}
                            value={statusOptions.find(opt => opt.value === localFilters.statusId) || null}
                            getOptionLabel={(option) => option.label}
                            onChange={(e, value) => {
                                setLocalFilters({
                                    ...localFilters,
                                    statusId: value?.value
                                });
                            }}
                        />
                    </div>

                    <div className='d-flex justify-content-end'>
                        <button
                            type='reset'
                            className='btn btn-sm btn-white btn-active-light-primary me-2'
                            data-kt-menu-dismiss='true'
                            onClick={handleResetFilters}
                        >
                            {t('generalMessages.reset')}
                        </button>
                        <button
                            type='button'
                            onClick={handleApplyFilters}
                            className='btn btn-sm btn-primary'
                            data-kt-menu-dismiss='true'
                        >
                            {t('generalMessages.apply')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}